// Pure form helpers used by the Vuex store. Keeping these functions free of UI
// dependencies makes the state layer easier to read and test.
export const HTTP_TYPES = ["GET", "POST", "PUT", "PATCH", "DELETE"];
export const DB_RULE_OPERATORS = ["=", "!=", ">", ">=", "<", "<="];

export function blankRuleAssertion(field = "") {
  return {
    field,
    operator: "=",
    expected: "",
  };
}

export function blankJobForm() {
  return {
    id: "",
    step: "",
    step_desc: "",
    url: "",
    http_type: "GET",
    return_rule: "",
    return_rule_end: 1,
    testCode: "{}",
    requestHeaders: "{}",
    requestParams: "{}",
    requestBody: "{}",
  };
}

export function blankRuleForm() {
  return {
    id: "",
    jobId: "",
    database: "",
    table: "",
    filter: "",
    assertions: [blankRuleAssertion()],
    rule: "",
  };
}

export function blankFlowForm() {
  return {
    id: "",
    name: "",
    description: "",
    steps: [],
    bindSteps: [],
    afterStep: "",
  };
}

export function parseDbAssertRule(ruleText) {
  try {
    const payload = JSON.parse(String(ruleText || "").trim());
    if (!payload || typeof payload !== "object" || payload.type !== "db_assert") {
      return null;
    }
    return payload;
  } catch (error) {
    return null;
  }
}

function cleanSqlIdentifier(value) {
  return String(value || "")
    .trim()
    .replace(/^[`"]|[`"]$/g, "");
}

function splitSqlList(value) {
  const items = [];
  let current = "";
  let depth = 0;
  let quote = "";

  for (const char of String(value || "")) {
    if (quote) {
      current += char;
      if (char === quote) {
        quote = "";
      }
      continue;
    }
    if (["'", '"', "`"].includes(char)) {
      quote = char;
      current += char;
      continue;
    }
    if (char === "(") {
      depth += 1;
    } else if (char === ")" && depth > 0) {
      depth -= 1;
    }
    if (char === "," && depth === 0) {
      items.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }

  if (current.trim()) {
    items.push(current.trim());
  }
  return items;
}

function sqlProjectionField(value) {
  const withoutAlias = String(value || "")
    .trim()
    .replace(/\s+as\s+[`"]?[A-Za-z_][A-Za-z0-9_]*[`"]?$/i, "")
    .replace(/\s+[`"]?[A-Za-z_][A-Za-z0-9_]*[`"]?$/i, "")
    .trim();
  if (!withoutAlias || withoutAlias === "*" || withoutAlias.includes("(")) {
    return "";
  }
  const parts = withoutAlias.split(".").map(cleanSqlIdentifier).filter(Boolean);
  return parts[parts.length - 1] || "";
}

// Parse a simple SELECT query into the Rule builder fields. This intentionally
// supports the Rule 管理 input shape instead of becoming a general SQL parser.
export function parseSelectRuleQuery(sqlText) {
  const statement = String(sqlText || "").trim().replace(/;+\s*$/, "");
  const selectMatch = statement.match(/^select\s+/i);
  const fromMatch = statement.match(/\sfrom\s/i);
  if (!selectMatch || !fromMatch || fromMatch.index <= selectMatch[0].length) {
    throw new Error("请输入 SELECT 查询语句");
  }

  const projection = statement.slice(selectMatch[0].length, fromMatch.index).trim();
  const afterFrom = statement.slice(fromMatch.index + fromMatch[0].length).trim();
  const targetMatch = afterFrom.match(/^\S+/);
  if (!targetMatch) {
    throw new Error("查询语句缺少表名");
  }

  const target = targetMatch[0];
  const suffix = afterFrom.slice(target.length);
  const targetParts = target.split(".").map(cleanSqlIdentifier).filter(Boolean);
  const table = targetParts.pop() || "";
  const database = targetParts.pop() || "";
  if (!table) {
    throw new Error("查询语句缺少表名");
  }

  let rest = String(suffix || "").trim();
  if (rest && !/^(where|group\s+by|order\s+by|limit)\b/i.test(rest)) {
    rest = rest.replace(/^(?:as\s+)?[`"]?[A-Za-z_][A-Za-z0-9_]*[`"]?\s*/i, "").trim();
  }

  let filter = "";
  if (/^where\b/i.test(rest)) {
    filter = rest
      .replace(/^where\s+/i, "")
      .replace(/\s+(group\s+by|order\s+by|limit)\b[\s\S]*$/i, "")
      .trim();
  }

  const assertions = splitSqlList(projection)
    .map(sqlProjectionField)
    .filter(Boolean)
    .map((field) => blankRuleAssertion(field));

  return { database, table, filter, assertions };
}

// Convert a saved testrule row back into the structured rule-builder form.
export function ruleFormFromRule(rule) {
  const form = blankRuleForm();
  form.id = rule.id;
  form.jobId = rule.job_id ? String(rule.job_id) : "";
  form.rule = rule.rule || "";

  const parsed = parseDbAssertRule(rule.rule);
  if (!parsed) {
    return form;
  }

  form.database = parsed.database || "";
  form.table = parsed.table || "";
  form.filter = parsed.filter || "";
  if (Array.isArray(parsed.assertions) && parsed.assertions.length) {
    form.assertions = parsed.assertions.map((assertion) => ({
      field: assertion.field || "",
      operator: DB_RULE_OPERATORS.includes(assertion.operator) ? assertion.operator : "=",
      expected:
        assertion.expected === undefined || assertion.expected === null
          ? ""
          : String(assertion.expected),
    }));
  } else {
    form.assertions = [
      {
        field: parsed.field || "",
        operator: DB_RULE_OPERATORS.includes(parsed.operator) ? parsed.operator : "=",
        expected: parsed.expected === undefined || parsed.expected === null ? "" : String(parsed.expected),
      },
    ];
  }
  return form;
}

export function hasStructuredRule(ruleForm) {
  const hasTarget = ["database", "table", "filter"].some((field) => String(ruleForm[field] || "").trim());
  const hasAssertions = Array.isArray(ruleForm.assertions)
    ? ruleForm.assertions.some(
        (assertion) =>
          String(assertion.field || "").trim() || String(assertion.expected || "").trim(),
      )
    : false;
  return hasTarget || hasAssertions;
}

export function normalizedRuleAssertions(ruleForm) {
  if (!Array.isArray(ruleForm.assertions)) {
    return [];
  }
  return ruleForm.assertions
    .map((assertion) => ({
      field: String(assertion.field || "").trim(),
      operator: String(assertion.operator || "=").trim(),
      expected: String(assertion.expected || "").trim(),
    }))
    .filter((assertion) => assertion.field || assertion.expected);
}

// Prefer structured db_assert JSON when the rule builder fields are used.
// Plain text rules are still preserved for backwards compatibility.
export function buildRuleText(ruleForm) {
  if (!hasStructuredRule(ruleForm)) {
    return String(ruleForm.rule || "").trim();
  }

  return JSON.stringify(
    {
      type: "db_assert",
      database: String(ruleForm.database || "").trim(),
      table: String(ruleForm.table || "").trim(),
      filter: String(ruleForm.filter || "").trim(),
      assertions: normalizedRuleAssertions(ruleForm),
    },
    null,
    2,
  );
}

export function prettyJson(value) {
  return JSON.stringify(value || {}, null, 2);
}

export function parseJsonField(value, fieldName) {
  const raw = String(value || "").trim();
  if (!raw) {
    return {};
  }
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(`${fieldName} 必须是 JSON 对象`);
  }
  return parsed;
}

export function splitTestCode(testCode, method) {
  let payload = {};
  try {
    payload = JSON.parse(testCode || "{}");
  } catch (error) {
    return {
      requestHeaders: "{}",
      requestParams: "{}",
      requestBody: "{}",
    };
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      requestHeaders: "{}",
      requestParams: "{}",
      requestBody: "{}",
    };
  }

  // Old jobs stored a flat JSON object. Newer jobs split query params and body
  // so GET/DELETE and POST-like methods can be edited consistently.
  const hasStructuredKeys = ["headers", "params", "body", "json"].some((key) => key in payload);
  if (hasStructuredKeys) {
    return {
      requestHeaders: prettyJson(payload.headers),
      requestParams: prettyJson(payload.params),
      requestBody: prettyJson(payload.body ?? payload.json),
    };
  }

  if (["GET", "DELETE"].includes(method)) {
    return {
      requestHeaders: "{}",
      requestParams: prettyJson(payload),
      requestBody: "{}",
    };
  }

  return {
    requestHeaders: "{}",
    requestParams: "{}",
    requestBody: prettyJson(payload),
  };
}

export function requestHeadersFromJob(job, fallback) {
  const raw = String(job.request_headers || "").trim();
  if (!raw) {
    return fallback || "{}";
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return fallback || "{}";
    }
    return prettyJson(parsed);
  } catch (error) {
    return fallback || "{}";
  }
}

export function buildTestCode(jobForm) {
  const params = parseJsonField(jobForm.requestParams, "请求参数");
  const body = parseJsonField(jobForm.requestBody, "请求体");
  return JSON.stringify({ params, body }, null, 2);
}

export function formFromJob(job) {
  const requestParts = splitTestCode(job.testCode, job.http_type || "GET");
  return {
    id: job.id,
    step: job.step || "",
    step_desc: job.step_desc || "",
    url: job.url || "",
    http_type: job.http_type || "GET",
    return_rule: job.return_rule || "",
    return_rule_end: Number(job.return_rule_end) === 0 ? 0 : 1,
    testCode: job.testCode || "{}",
    requestHeaders: requestHeadersFromJob(job, requestParts.requestHeaders),
    requestParams: requestParts.requestParams,
    requestBody: requestParts.requestBody,
  };
}

export function formFromCurlJob(job, currentForm = blankJobForm()) {
  return {
    ...blankJobForm(),
    step: currentForm.step || job.step || "",
    step_desc: currentForm.step_desc || job.step_desc || "",
    url: job.url || "",
    http_type: HTTP_TYPES.includes(job.http_type) ? job.http_type : "GET",
    return_rule: currentForm.return_rule || job.return_rule || "",
    return_rule_end: Number(currentForm.return_rule_end) === 0 ? 0 : 1,
    testCode: job.testCode || "{}",
    requestHeaders: job.requestHeaders || job.request_headers || "{}",
    requestParams: job.requestParams || "{}",
    requestBody: job.requestBody || "{}",
  };
}

// Build the backend payload from the editor form. Headers are stored separately
// when the backend schema supports request_headers.
export function jobPayload(jobForm) {
  const headers = parseJsonField(jobForm.requestHeaders, "请求头");
  const testCode = buildTestCode(jobForm);
  return {
    step: jobForm.step,
    step_desc: jobForm.step_desc,
    url: jobForm.url,
    http_type: jobForm.http_type,
    request_headers: JSON.stringify(headers, null, 2),
    return_rule: jobForm.return_rule,
    return_rule_end: Number(jobForm.return_rule_end) === 0 ? 0 : 1,
    testCode,
  };
}

export function flowPayload(flowForm) {
  return {
    name: String(flowForm.name || "").trim(),
    description: String(flowForm.description || "").trim(),
    steps: normalizeSteps(flowForm.steps),
  };
}

export function normalizeSteps(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const seen = new Set();
  return values
    .map((step) => String(step || "").trim())
    .filter((step) => {
      if (!step || seen.has(step)) {
        return false;
      }
      seen.add(step);
      return true;
    });
}

export function moveFlowStepAt(steps, index, offset) {
  const normalized = normalizeSteps(steps);
  const targetIndex = index + offset;
  if (targetIndex < 0 || targetIndex >= normalized.length) {
    return normalized;
  }
  const moved = normalized[index];
  normalized[index] = normalized[targetIndex];
  normalized[targetIndex] = moved;
  return normalized;
}

export function removeFlowStepAt(steps, index) {
  const normalized = normalizeSteps(steps);
  normalized.splice(index, 1);
  return normalized;
}

// Drag-and-drop reports the row being dropped on. Keep that row as the visual
// insertion point so dragging down lands immediately before the target row.
export function dropFlowStepAt(steps, sourceIndex, targetIndex) {
  const normalized = normalizeSteps(steps);
  if (sourceIndex === null || sourceIndex === targetIndex) {
    return normalized;
  }
  const sourceOutOfRange = sourceIndex < 0 || sourceIndex >= normalized.length;
  const targetOutOfRange = targetIndex < 0 || targetIndex >= normalized.length;
  if (sourceOutOfRange || targetOutOfRange) {
    return normalized;
  }
  const moved = normalized.splice(sourceIndex, 1)[0];
  const nextIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
  normalized.splice(nextIndex, 0, moved);
  return normalized;
}

// Merge selected steps into the flow while keeping them unique. If afterStep is
// blank or not in the current flow, additions are appended.
export function mergeFlowSteps(currentSteps, addingSteps, afterStep) {
  const additions = normalizeSteps(addingSteps);
  if (!additions.length) {
    return normalizeSteps(currentSteps);
  }

  const addingSet = new Set(additions);
  const merged = normalizeSteps(currentSteps).filter((step) => !addingSet.has(step));
  const insertAfter = String(afterStep || "").trim();
  const insertIndex = insertAfter ? merged.indexOf(insertAfter) + 1 : 0;
  if (insertIndex > 0) {
    merged.splice(insertIndex, 0, ...additions);
  } else {
    merged.push(...additions);
  }
  return merged;
}
